import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { debounce } from "lodash";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";

/**
 * Toolbar search field (Innovation Lab style: left icon, border, debounced updates).
 *
 * Presentation only: the parent decides how search text is stored and how queries run
 * (Redux `setSearchTextByPage`, React Query `setSearch`, URL params, local state, etc.).
 *
 * @param {object} props
 * @param {string} [props.value] — Optional controlled sync value (e.g. from Redux). When it changes externally, the input updates.
 * @param {string} [props.defaultValue] — Initial value when `value` is omitted (uncontrolled).
 * @param {(text: string) => void} props.onDebouncedChange — Called with the input value (including leading/trailing spaces) after `debounceMs`. Wire this to your store or refetch trigger.
 * @param {number} [props.debounceMs]
 * @param {string} [props.placeholder]
 * @param {string} [props.className] — Wrapper
 * @param {string} [props.inputClassName] — Input element
 * @param {string} [props.id] — Optional id (defaults to React `useId()`)
 * @param {string} [props.ariaLabel]
 */
export default function ToolbarSearch({
  value: valueProp,
  defaultValue = "",
  onDebouncedChange,
  debounceMs = 300,
  placeholder = "Search...",
  className = "",
  inputClassName = "",
  id: idProp,
  ariaLabel = "Search",
}) {
  const reactId = useId();
  const id = idProp ?? `toolbar-search-${reactId.replace(/:/g, "")}`;

  const isControlledSync = valueProp !== undefined;
  const [localValue, setLocalValue] = useState(() =>
    isControlledSync ? valueProp : defaultValue
  );

  useEffect(() => {
    if (isControlledSync) {
      setLocalValue(valueProp);
    }
  }, [isControlledSync, valueProp]);

  const onDebouncedChangeRef = useRef(onDebouncedChange);
  onDebouncedChangeRef.current = onDebouncedChange;

  const debouncedEmit = useMemo(
    () =>
      debounce((text) => {
        onDebouncedChangeRef.current?.(text);
      }, debounceMs),
    [debounceMs]
  );

  useEffect(() => () => debouncedEmit.cancel(), [debouncedEmit]);

  const onChange = useCallback(
    (e) => {
      const v = e.target.value;
      setLocalValue(v);
      debouncedEmit(v);
    },
    [debouncedEmit]
  );

  return (
    <div className={`relative flex min-w-0 flex-1 ${className}`}>
      <label htmlFor={id} className="sr-only">
        {ariaLabel}
      </label>
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" aria-hidden />
      </div>
      <input
        id={id}
        name="search"
        type="search"
        autoComplete="off"
        placeholder={placeholder}
        value={localValue}
        onChange={onChange}
        aria-label={ariaLabel}
        className={`block w-full min-w-0 max-w-[360px] appearance-none rounded-md border border-solid border-gray-300 bg-white py-1.5 pl-9 text-sm leading-6 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 sm:text-sm [&::-webkit-search-cancel-button]:hidden ${inputClassName}`}
      />
    </div>
  );
}
